"use client";

// Extracted from page.tsx in D6a. Renders the PayPal Smart Buttons
// (lazy-loaded via dynamic script tag) and handles the create-order
// / approve flow against the Go API. Order capture happens
// server-side via /paypal/capture-order.

import { useEffect, useRef, useState } from "react";
import { loadPayPalSdk } from "@/lib/paypal-sdk-loader";
import { consentMetadata } from "@/lib/consent-state";
import { readAdIdentifierCookies } from "./_ad-ids";

const GO_API =
  process.env.NEXT_PUBLIC_API_URL || "https://crm-app.makedesignerbags.com";

// BumpOffer shape mirrors the one in page.tsx byte-for-byte
// (loose `string` for `type` etc.) so cross-file passing typechecks
// cleanly. If a third caller appears, promote to a shared types file.
interface BumpOffer {
  id: string;
  type: string;
  gift_name: string;
  gift_description: string;
  gift_image_url: string;
  addon_price_cents: number;
  original_price_cents: number;
  threshold_cents: number;
  emoji: string;
  discount_type: string;
  discount_value: number;
}

export function PayPalSection({
  productIds,
  quantities,
  lpId,
  visitorId,
  email,
  currency,
  slug,
  selectedAddons,
  bumpOffers,
  amountOverrideCents,
  fxRate,
  firstName,
  lastName,
  billingCountry,
  clientId,
  paypalEnv,
  payLater = false,
  extraMetadata,
  onEmailRequired,
  onPaymentInitiated,
  buttonColor = "gold",
  buttonLabel = "paypal",
  buttonLayout = "horizontal",
  buttonHeight = 48,
  funnelMode = "page",
}: {
  /** Surface label on the funnel events — "island" when rendered inside the
   *  inline checkout, so reporting can tell the two surfaces apart. */
  funnelMode?: "page" | "island";
  productIds: string[];
  quantities: number[];
  lpId: string;
  visitorId: string;
  email: string;
  currency: string;
  slug: string;
  selectedAddons: Set<string>;
  bumpOffers: BumpOffer[];
  amountOverrideCents: number;
  /** Rate behind amountOverrideCents (chargeFxRate); 0 for USD. */
  fxRate: number;
  firstName: string;
  lastName: string;
  billingCountry: string;
  clientId: string;
  paypalEnv: "live" | "sandbox";
  /** Installments: load the SDK with Pay Later funding so PayPal can render
   *  its own "Pay in 4 / Pay Monthly" option on the button.
   *  Gated by the API's installments_enabled; false → the SDK URL is
   *  byte-identical to before, so nothing about the button changes. Whether
   *  Pay Later actually renders is PayPal's call (country + amount band).
   *
   *  Funding only — no `components=` parameter. Nothing here renders
   *  paypal.Messages, and passing `components=` REPLACES the SDK's default
   *  component set rather than adding to it, so naming a subset is a way to
   *  lose a component we do use, for no gain. */
  payLater?: boolean;
  // Checkout A/B variant etc., merged into orders.metadata (never into amounts).
  extraMetadata?: Record<string, string>;
  // Button styling — defaults to the gold "PayPal" pill used on /checkout.
  // The native inline checkout overrides to a blue full-width "Pay with PayPal"
  // button (color:"blue", label:"pay") to match the accordion mockup.
  buttonColor?: "gold" | "blue" | "silver" | "white" | "black";
  buttonLabel?: "paypal" | "checkout" | "buynow" | "pay" | "installment";
  buttonLayout?: "horizontal" | "vertical";
  buttonHeight?: number;
  // Called when the buyer clicks PayPal without a (valid) typed email — the
  // page routes them to the email field. Typed email is REQUIRED for PayPal:
  // it is the contact identity and feeds the abandoned-cart sequence.
  onEmailRequired?: () => void;
  // AddPaymentInfo hook (Shopify parity): called when the buyer actually
  // clicks the PayPal button (payment submission moment).
  onPaymentInitiated?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onEmailRequiredRef = useRef(onEmailRequired);
  onEmailRequiredRef.current = onEmailRequired;
  const onPaymentInitiatedRef = useRef(onPaymentInitiated);
  onPaymentInitiatedRef.current = onPaymentInitiated;
  const [loaded, setLoaded] = useState(false);
  const [ppError, setPpError] = useState("");
  // SDK script failed to load (ad-blocker / in-app browser) — show a clear
  // "pay by card" hint instead of the eternal "Loading PayPal..." text.
  const [sdkFailed, setSdkFailed] = useState(false);
  // Scroll the payment error into view when it appears — it renders below the
  // buttons and is easy to miss on small screens.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ppError) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [ppError]);
  // Set when createOrder aborts on a missing field, so PayPal's onError (which
  // fires right after the createOrder throw) doesn't clobber our specific
  // validation message with the generic "payment failed" one.
  const validationFailedRef = useRef(false);

  // Refs so PayPal's createOrder callback always reads current billing fields,
  // even though the Buttons() handlers are only registered once per SDK load.
  const firstNameRef = useRef(firstName);
  const lastNameRef = useRef(lastName);
  const billingCountryRef = useRef(billingCountry);
  const emailRef = useRef(email);
  const visitorIdRef = useRef(visitorId);
  const selectedAddonsRef = useRef(selectedAddons);
  const amountOverrideRef = useRef(amountOverrideCents);
  const fxRateRef = useRef(fxRate);
  useEffect(() => { visitorIdRef.current = visitorId; }, [visitorId]);
  useEffect(() => { firstNameRef.current = firstName; }, [firstName]);
  useEffect(() => { lastNameRef.current = lastName; }, [lastName]);
  useEffect(() => { billingCountryRef.current = billingCountry; }, [billingCountry]);
  useEffect(() => { emailRef.current = email; }, [email]);
  useEffect(() => { selectedAddonsRef.current = selectedAddons; }, [selectedAddons]);
  useEffect(() => { amountOverrideRef.current = amountOverrideCents; }, [amountOverrideCents]);
  const extraMetadataRef = useRef(extraMetadata);
  useEffect(() => { extraMetadataRef.current = extraMetadata; }, [extraMetadata]);
  useEffect(() => { fxRateRef.current = fxRate; }, [fxRate]);

  // Reload the PayPal SDK whenever currency or workspace credentials change.
  // The SDK's currency is baked into the script URL, so we must re-request
  // and re-render. clientId + paypalEnv are workspace-scoped — they come
  // from the integrations row via /api/v1/checkout/public-config, NOT from
  // a Vercel-wide env var (which would break the moment two workspaces use
  // different PayPal apps).
  useEffect(() => {
    if (!clientId) return;

    setLoaded(false);
    setSdkFailed(false);

    // PayPal's SDK auto-routes to sandbox vs live based on the clientId
    // prefix; we don't need to pass env explicitly. paypalEnv is kept in
    // the API for completeness (e.g. UI badges) but not used here.
    void paypalEnv;

    // Shared loader: reuses the already-loaded SDK on remounts (plan switch)
    // — re-evaluating the script crashes zoid and the buttons never render.
    let cancelled = false;
    const tryRender = (attempt: number) => {
      if (cancelled) return;
      const pp = (window as unknown as Record<string, unknown>).paypal;
      if ((!pp || !containerRef.current) && attempt < 40) {
        // Container not committed yet (plan-switch remount race) — retry
        // briefly instead of bailing forever.
        window.setTimeout(() => tryRender(attempt + 1), 150);
        return;
      }
      renderButtons();
    };
    // Pay Later changes the SDK URL, so the shared loader treats it as a
    // genuinely different SDK and re-bootstraps cleanly (see paypal-sdk-loader
    // rule 2) rather than reusing a window.paypal built without the funding.
    const sdkSrc =
      `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency.toUpperCase()}` +
      (payLater ? "&enable-funding=paylater" : "");
    loadPayPalSdk(
      "paypal-sdk",
      sdkSrc,
      () => tryRender(0),
      () => {
        if (!cancelled) setSdkFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, clientId, paypalEnv, payLater]);

  function renderButtons() {
    const pp = (window as unknown as Record<string, unknown>).paypal as Record<string, unknown> | undefined;
    if (!pp || !containerRef.current) return;

    // Clear previous buttons
    containerRef.current.innerHTML = "";

    const Buttons = pp.Buttons as (opts: Record<string, unknown>) => { render: (el: HTMLElement) => void };
    Buttons({
      style: { layout: buttonLayout, color: buttonColor, shape: "rect", label: buttonLabel, height: buttonHeight },
      createOrder: async () => {
        // Email is OPTIONAL for PayPal: when the buyer didn't type one, the
        // server fills it from the PayPal payer email on capture. Last name +
        // country are optional too (backfilled in the CRM from the payer). So we
        // no longer block order creation on any of them.
        validationFailedRef.current = false;
        setPpError("");

        // Email is REQUIRED for PayPal: without a typed email the buyer is
        // routed to the email field (red border + "Enter an email") and no
        // PayPal order is created. The typed email is the contact identity
        // and feeds the abandoned-cart sequence.
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailRef.current.trim())) {
          validationFailedRef.current = true;
          onEmailRequiredRef.current?.();
          throw new Error("email_required");
        }

        // payment_method_selected — the PayPal button click is the selection
        // moment. Same LP funnel pipe as the page's trackFunnel; best-effort.
        onPaymentInitiatedRef.current?.();
        if (visitorIdRef.current && slug) {
          fetch(`${GO_API}/api/v1/lp/${encodeURIComponent(slug)}/events`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            keepalive: true,
            body: JSON.stringify({
              visitor_id: visitorIdRef.current,
              events: [
                { type: "payment_method_selected", data: { method: "paypal", mode: funnelMode }, ts: Date.now() },
                { type: "payment_initiated", data: { method: "paypal", mode: funnelMode }, ts: Date.now() },
              ],
            }),
          }).catch(() => {});
        }

        // Selected paid add-ons are bump_offers (NOT products) — send their ids
        // separately so the backend validates + prices them server-side. The
        // amount already includes add-ons via amountOverrideCents (grandTotal).
        const addonBumpIds = bumpOffers
          .filter((b) => b.type === "addon" && selectedAddonsRef.current.has(b.id))
          .map((b) => b.id);

        const res = await fetch(`${GO_API}/api/v1/paypal/create-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_ids: productIds,
            bump_offer_ids: addonBumpIds,
            quantities: productIds.map(() => 1),
            landing_page_id: lpId,
            visitor_id: visitorIdRef.current,
            customer_email: emailRef.current,
            customer_first_name: firstNameRef.current,
            customer_last_name: lastNameRef.current,
            customer_country: billingCountryRef.current,
            // Re-read at click time (same snapshot as CheckoutContent).
            ad_identifiers: readAdIdentifierCookies(),
            currency,
            amount_override_cents: amountOverrideRef.current,
            fx_rate: fxRateRef.current,
            // Object, not JSON.stringify'd: the backend stores this verbatim
            // in orders.metadata (JSONB), and the journey stitcher expects a
            // JSON object — a stringified value triggers a "malformed
            // metadata" warn and breaks visitor_id extraction.
            metadata: { slug, ...consentMetadata(), ...extraMetadataRef.current },
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error?.message || "PayPal error");
        return json.data.paypal_order_id;
      },
      onApprove: async (data: { orderID: string }) => {
        // MONEY MOMENT: the buyer has already approved in PayPal. A network
        // error or 5xx here is AMBIGUOUS — the server capture may have
        // succeeded (response lost) or the webhook may still complete the
        // order — so never show "failed, try another method" for it (that
        // invites a double payment). Only a clean 4xx decline keeps the
        // generic failed path via onError.
        const ambiguousMsg =
          "Your PayPal approval was received — we're confirming your payment. " +
          "Please check your email for a confirmation before trying again.";
        const capture = () =>
          fetch(`${GO_API}/api/v1/paypal/capture-order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paypal_order_id: data.orderID }),
          });
        let res: Response;
        try {
          try {
            res = await capture();
          } catch {
            // One automatic retry on a network-level failure only.
            res = await capture();
          }
        } catch {
          setPpError(ambiguousMsg);
          return;
        }
        const json = await res.json().catch(() => null);
        if (res.ok && json?.data?.order_id) {
          window.location.href = `/checkout/upsell?order_id=${json.data.order_id}`;
          return;
        }
        if (res.status >= 400 && res.status < 500) {
          // Clean decline from the capture endpoint — safe to invite a retry.
          throw new Error(json?.error?.message || "Capture failed");
        }
        // 5xx or unreadable success body: money may have moved.
        setPpError(ambiguousMsg);
      },
      onError: (err: unknown) => {
        // A validation abort from createOrder also surfaces here; keep our
        // specific message instead of the generic one.
        if (validationFailedRef.current) {
          validationFailedRef.current = false;
          return;
        }
        console.error("PayPal error:", err);
        setPpError("PayPal payment failed. Please try another method.");
      },
    }).render(containerRef.current!);

    setLoaded(true);
  }

  return (
    <>
      <div ref={containerRef} className="min-h-[48px]" />
      {sdkFailed && !loaded && (
        <div
          role="alert"
          className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
        >
          PayPal is unavailable right now — please pay by card instead.
        </div>
      )}
      {!loaded && !ppError && !sdkFailed && (
        <p className="text-xs text-gray-400 mt-2">Loading PayPal...</p>
      )}
      {ppError && (
        <div
          ref={errorRef}
          role="alert"
          className="mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600"
        >
          {ppError}
        </div>
      )}
    </>
  );
}
